<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <template v-if="loaded && data">
      <!-- ผลการตรวจภาพรวม -->
      <v-alert :type="verdict.type" outlined border="left" prominent class="mb-3">
        <div class="font-weight-bold">{{ verdict.title }}</div>
        <ul v-if="verdict.items.length" class="caption mt-1 mb-0">
          <li v-for="(t, i) in verdict.items" :key="i">{{ t }}</li>
        </ul>
        <div class="caption grey--text mt-1">
          ตรวจตามวันที่บรรจุครั้งแรก การพ้นสภาพ และสัญญาจ้างของพนักงานราชการแต่ละคน
          ({{ persons.length }} คน ตามกรอบอัตรากำลังและทะเบียนประวัติ ณ ปัจจุบัน)
        </div>
      </v-alert>

      <v-row>
        <v-col v-for="no in [1, 2]" :key="no" cols="12" md="6">
          <v-alert :type="roundVerdict(no).type" outlined dense border="left" class="mb-0 round-card">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <strong>รอบที่ {{ no }} ({{ data.rounds[no].start }} – {{ data.rounds[no].end }})</strong>
              <span>ประเมินแล้ว {{ sum(no).assessed }} / {{ sum(no).required }} คน</span>
            </div>
            <div class="caption">{{ roundVerdict(no).text }}</div>

            <div v-if="listBy(no, ['missing']).length" class="mt-2">
              <div class="caption font-weight-bold">ยังไม่รายงานผล ({{ listBy(no, ['missing']).length }} คน):</div>
              <v-chip
                v-for="p in listBy(no, ['missing'])"
                :key="'m' + no + p.id_card"
                small
                outlined
                color="error"
                class="mr-1 mb-1"
                @click="$emit('add', p, no)"
              >
                <v-icon x-small left>mdi-plus</v-icon>{{ p.full_name }}
                <span v-if="p['r' + no + '_need'] === 'mid'" class="ml-1">(บรรจุ {{ p.appoint_date }})</span>
              </v-chip>
            </div>

            <div v-if="listBy(no, ['not_due']).length" class="mt-2">
              <div class="caption font-weight-bold">ยังไม่สิ้นรอบ — รอรายงาน ({{ listBy(no, ['not_due']).length }} คน)</div>
            </div>

            <div v-if="listBy(no, ['draft', 'rejected', 'submitted']).length" class="mt-2">
              <div class="caption font-weight-bold">
                บันทึกแล้วแต่ยังไม่อนุมัติ ({{ listBy(no, ['draft', 'rejected', 'submitted']).length }} คน):
              </div>
              <v-chip
                v-for="p in listBy(no, ['draft', 'rejected', 'submitted'])"
                :key="'d' + no + p.id_card"
                small
                outlined
                :color="p['r' + no + '_status'] === 'submitted' ? 'info' : 'warning'"
                class="mr-1 mb-1"
              >
                {{ p.full_name }} · {{ statusText(p['r' + no + '_status']) }}
              </v-chip>
            </div>

            <div v-if="excused(no).length" class="mt-2">
              <div class="caption font-weight-bold grey--text">ไม่ต้องรายงานรอบนี้ ({{ excused(no).length }} คน):</div>
              <v-tooltip v-for="p in excused(no)" :key="'x' + no + p.id_card" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip small class="mr-1 mb-1" color="grey lighten-3" v-bind="attrs" v-on="on">
                    {{ p.full_name }}
                  </v-chip>
                </template>
                <span>{{ needText(p, no) }}</span>
              </v-tooltip>
            </div>

            <div v-if="midJoiners(no).length" class="caption grey--text mt-1">
              บรรจุระหว่างรอบ (ต้องประเมินตามระยะเวลาที่ปฏิบัติงาน):
              {{ midJoiners(no).map(p => `${p.full_name} บรรจุ ${p.appoint_date} (${p['r' + no + '_months']} เดือน)`).join(", ") }}
            </div>
            <div v-if="sum(no).from_other" class="caption purple--text mt-1">
              ใช้ผลประเมินจากสถานศึกษาเดิม (ย้ายมา) {{ sum(no).from_other }} คน
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- ข้อมูลที่ต้องตรวจสอบ -->
      <v-expansion-panels v-if="issuePersons.length || (data.outside_roster || []).length" class="mt-3" flat>
        <v-expansion-panel class="issue-panel">
          <v-expansion-panel-header class="py-1">
            <span class="warning--text text--darken-2">
              <v-icon small color="warning">mdi-alert</v-icon>
              ข้อมูลที่ต้องตรวจสอบ {{ issuePersons.length + (data.outside_roster || []).length }} รายการ
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense>
              <tbody>
                <tr v-for="p in issuePersons" :key="'i' + p.id_card">
                  <td style="width:260px">{{ p.full_name }}<div class="caption grey--text">{{ p.id_card }}</div></td>
                  <td class="caption">
                    บรรจุครั้งแรก {{ p.appoint_date || "-" }}
                    <span v-if="p.contract_start || p.contract_end"> · สัญญา {{ p.contract_start || "?" }} – {{ p.contract_end || "?" }}</span>
                    <span v-if="p.resignation_date"> · ลาออก {{ p.resignation_date }}</span>
                  </td>
                  <td class="caption warning--text text--darken-2">{{ p.issues }}</td>
                </tr>
                <tr v-for="a in data.outside_roster || []" :key="'o' + a.id">
                  <td>{{ a.id_card }}</td>
                  <td class="caption">ผลประเมินรอบ {{ a.assessment_round }} ({{ statusText(a.assessment_status) }})</td>
                  <td class="caption warning--text text--darken-2">
                    ไม่อยู่ในรายชื่อพนักงานปัจจุบันของหน่วยงาน (ย้ายออก/พ้นสภาพ/ไม่อยู่ในกรอบ) — ตรวจสอบความถูกต้อง
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </div>
</template>

<script>
// ตรวจความครบถ้วนของการรายงานผลการประเมิน (แบบ ปผ.พนร.) ของสถานศึกษา/สถาบัน ปีละ 2 รอบ
// กติกา "ใครต้องรายงานรอบไหน" อยู่ฝั่งเซิร์ฟเวอร์ (gov_em_assessment_rules.php) ใช้ร่วมกับหน้าส่วนกลาง
const API = "gov_em_assessment_overview.php";
const COUNTED = ["required", "mid", "unknown"];
const STATUS = {
  approved: "อนุมัติ",
  submitted: "ส่งแล้ว รออนุมัติ",
  draft: "ร่าง",
  rejected: "ส่งกลับแก้ไข",
  missing: "ยังไม่รายงาน",
  not_due: "ยังไม่สิ้นรอบ",
  not_required: "ไม่ต้องรายงาน"
};

export default {
  name: "AssessmentCompleteness",
  props: {
    year: { type: [Number, String], required: true },
    // เปลี่ยนค่าเมื่อหน้าแม่โหลดผลประเมินใหม่ เพื่อให้ตรวจซ้ำ
    refreshKey: { type: [Number, String], default: 0 }
  },

  data() {
    return { loading: false, loaded: false, data: null };
  },

  computed: {
    persons() {
      return (this.data && this.data.persons) || [];
    },
    issuePersons() {
      return this.persons.filter(p => p.issues);
    },
    verdict() {
      const items = [];
      let pendingRounds = 0;
      [1, 2].forEach(no => {
        const s = this.sum(no);
        const r = this.data.rounds[no];
        if (!r.started) return;
        if (s.missing) items.push(`รอบที่ ${no}: ยังไม่รายงานผล ${s.missing} คน`);
        if (s.draft + s.rejected) items.push(`รอบที่ ${no}: ยังเป็นร่าง/ส่งกลับแก้ไข ${s.draft + s.rejected} คน`);
        if (s.submitted) items.push(`รอบที่ ${no}: ส่งแล้วรออนุมัติ ${s.submitted} คน`);
        if (s.extra) items.push(`รอบที่ ${no}: มีผลประเมินของผู้ที่ไม่ต้องประเมิน ${s.extra} คน (บรรจุหลังสิ้นรอบ/พ้นสภาพ)`);
        if (!r.ended) pendingRounds++;
      });
      const dataIssues = this.issuePersons.length + ((this.data && this.data.outside_roster) || []).length;
      if (dataIssues) items.push(`ข้อมูลพนักงานที่ต้องตรวจสอบ ${dataIssues} รายการ (ดูรายละเอียดด้านล่าง)`);

      const blocking = items.length;
      if (!blocking && !pendingRounds) {
        return { type: "success", title: `การรายงานผลการประเมินปีงบประมาณ ${this.year} ครบถ้วนสมบูรณ์ทั้ง 2 รอบ`, items };
      }
      if (!blocking) {
        return { type: "info", title: `รายงานครบถ้วนในรอบที่สิ้นสุดแล้ว — รอบปัจจุบันยังไม่สิ้นสุด`, items };
      }
      return { type: "warning", title: `การรายงานผลการประเมินปีงบประมาณ ${this.year} ยังไม่ครบถ้วน กรุณาตรวจสอบ`, items };
    }
  },

  watch: {
    year() {
      this.load();
    },
    refreshKey() {
      this.load();
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    sum(no) {
      return (this.data && this.data.summary && this.data.summary[no]) || {};
    },
    listBy(no, statuses) {
      return this.persons.filter(
        p => COUNTED.includes(p["r" + no + "_need"]) && statuses.includes(p["r" + no + "_status"])
      );
    },
    excused(no) {
      return this.persons.filter(p => !COUNTED.includes(p["r" + no + "_need"]));
    },
    midJoiners(no) {
      return this.persons.filter(p => p["r" + no + "_need"] === "mid");
    },
    statusText(s) {
      return STATUS[s] || s;
    },
    needText(p, no) {
      const need = p["r" + no + "_need"];
      if (need === "not_required") return `บรรจุครั้งแรก ${p.appoint_date} หลังสิ้นรอบที่ ${no} — ไม่ต้องประเมินรอบนี้`;
      if (need === "left_before") return `พ้นสภาพ ${p.resignation_date} ก่อนเริ่มรอบที่ ${no}`;
      if (need === "left_mid") return `พ้นสภาพ ${p.resignation_date} ระหว่างรอบที่ ${no} — ไม่บังคับรายงาน`;
      return "";
    },
    roundVerdict(no) {
      const s = this.sum(no);
      const r = this.data.rounds[no];
      if (!r.started) return { type: "info", text: "ยังไม่เริ่มรอบ" };
      if (s.missing || s.draft || s.rejected) return { type: "warning", text: "ยังไม่ครบถ้วน — ตรวจสอบรายชื่อด้านล่าง" };
      if (!r.ended) return { type: "info", text: "อยู่ระหว่างรอบการประเมิน" };
      if (s.submitted) return { type: "info", text: "รายงานครบแล้ว รอผู้บริหารอนุมัติ" };
      return { type: "success", text: s.required ? "รายงานผลครบถ้วนแล้ว" : "ไม่มีผู้ที่ต้องรายงานในรอบนี้" };
    },
    async load() {
      if (!this.year) return;
      this.loading = true;
      try {
        const res = await this.$http.post(API, { ApiKey: "HRvec2021", action: "persons", comp_year: Number(this.year) });
        if (!res.data.status) throw new Error(res.data.message);
        this.data = res.data;
      } catch (e) {
        this.data = null;
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    }
  }
};
</script>

<style scoped>
.round-card {
  height: 100%;
}
.issue-panel {
  border: 1px solid #ffb74d;
}
</style>
